<template>
  <div :class="$style.grid_editor_container">
    <component
      :is="customFieldComponentName"
      ref="inlineEditorCustomColumn"
      :style="customFieldComponentStyle"
      v-bind="customFieldComponentProps"
      v-on="customFieldComponentListeners"
    />
  </div>
</template>

<script>
import GridInput from '../components/input.vue';
import GridSelect from '../components/select.vue';
import GridColorPicker from '../components/colorPicker.vue';
import GridDatePicker from '../components/customDatePicker.vue';

export default {
  name: 'GridCustomCellEditor',
  components: {
    'grid-color-picker': GridColorPicker,
    'grid-select': GridSelect,
    'grid-input': GridInput,
    'grid-date-picker': GridDatePicker,
  },
  props: {
    column: {
      type: Object,
      required: true,
    },
    placeholder: {
      type: Object,
      required: true,
    },
    options: {
      type: Array,
      required: true,
    },
    onSave: {
      type: Function,
      required: true,
    },
    onCancel: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      value: '',
      datePickerValue: null,
    };
  },
  computed: {
    customFieldComponentName() {
      return this.customFieldComponentConfig.name;
    },
    customFieldComponentListeners() {
      return this.customFieldComponentConfig.listeners || {};
    },
    customFieldComponentProps() {
      return this.customFieldComponentConfig.props || {};
    },
    customFieldComponentStyle() {
      return this.customFieldComponentConfig.style || {};
    },
    customFieldComponentConfig() {
      const COMPONENTS_CONFIGS = {
        custom_color: this.getColorComponentConfig,
        custom_text: this.getTextComponentConfig,
        custom_multiselect: this.getMultiSelectComponentConfig,
        custom_select: this.getSelectComponentConfig,
        custom_tags: this.getTagsComponentConfig,
        custom_resources: this.getResourcesComponentConfig,
        custom_number: this.getNumberComponentConfig,
        custom_date: this.getDateComponentConfig,
      };

      if (COMPONENTS_CONFIGS[this.column.data_type]) {
        return COMPONENTS_CONFIGS[this.column.data_type]();
      }

      return {};
    },
  },
  mounted() {
    this.placeholder.addEventListener('wheel', this.onScrollPlaceholder, { passive: true });
  },
  beforeUnmount() {
    this.placeholder.removeEventListener('wheel', this.onScrollPlaceholder);
  },
  methods: {
    getTextComponentConfig() {
      return {
        name: 'grid-input',
        props: {
          type: 'text',
          value: this.value,
          initialSelect: true,
          textAlign: 'start',
        },
        listeners: {
          onInput: value => {
            this.value = value.toString();
          },
        },
      };
    },
    getNumberComponentConfig() {
      return {
        name: 'grid-input',
        props: {
          type: 'number',
          value: this.value,
          minValue: 0,
          initialSelect: true,
          textAlign: 'center',
        },
        listeners: {
          onInput: value => {
            this.value = value;
          },
        },
      };
    },
    getResourcesComponentConfig() {
      return {
        name: 'grid-select',
        props: {
          options: this.getOptionsForResourcesMultiselect(),
          data: this.getSelectedOptionsForResourcesMultiselect(),
          groupBy: 'group',
          itemTemplate: 'user',
          multiselect: true,
          placeholder: this.placeholder,
        },
        listeners: {
          onChange: value => {
            this.value = value.join(',');
          },
        },
      };
    },
    getTagsComponentConfig() {
      return {
        name: 'grid-select',
        props: {
          options: this.options,
          data: this.getSelectedOptionsForMultiSelect(),
          itemTemplate: 'label',
          trackByText: 'value',
          multiselect: true,
          placeholder: this.placeholder,
        },
        listeners: {
          onChange: value => {
            this.value = value.join(',');
          },
        },
      };
    },
    getSelectComponentConfig() {
      return {
        name: 'grid-select',
        props: {
          options: this.options,
          data: this.getSelectedOptionForSelect(),
          multiselect: false,
          placeholder: this.placeholder,
          trackByText: 'value',
        },
        listeners: {
          onChange: value => {
            this.value = value.join(',');
            this.onSave();
          },
        },
      };
    },
    getMultiSelectComponentConfig() {
      return {
        name: 'grid-select',
        props: {
          options: this.options,
          data: this.getSelectedOptionsForMultiSelect(),
          trackByText: 'value',
          multiselect: true,
          placeholder: this.placeholder,
        },
        listeners: {
          onChange: value => {
            this.value = value.join(',');
          },
        },
      };
    },
    getColorComponentConfig() {
      return {
        name: 'grid-color-picker',
        props: {
          options: this.options,
          data: this.value,
          placeholder: this.placeholder,
        },
        listeners: {
          selectColors: value => {
            this.value = value.toString();
            this.onSave();
          },
        },
      };
    },
    getSelectedOptionsForMultiSelect() {
      if (!this.value) return;

      const selectedIds = this.value.toString().split(',').map(item => Number(item));

      return selectedIds.reduce((result, id) => {
        const foundOption = this.options.find(option => option.id == id);

        if (foundOption) {
          result.push(foundOption);
        }

        return result;
      }, []);
    },
    getOptionsForResourcesMultiselect() {
      return this.options.map(item => ({
        id: item.id,
        name: item.name,
        img: item.picture,
        group: item.userId ? __('resources_layout_header_title_1') : __('resources_layout_header_title_2'),
      }));
    },
    getSelectedOptionsForResourcesMultiselect() {
      if (!this.value) return;

      const selectedIds = this.value.toString().split(',').map(Number);

      return this.getOptionsForResourcesMultiselect().filter(option => selectedIds.includes(option.id));
    },
    getSelectedOptionForSelect() {
      if (!this.value) return;

      return this.options.find(option => option.id == this.value);
    },
    getDateComponentConfig() {
      return {
        name: 'grid-date-picker',
        props: {
          value: this.value,
          datePickerValue: this.datePickerValue,
          selectedDate: !!this.value,
          placeholder: this.placeholder,
        },
        listeners: {
          onInput: val => {
            this.value = val;
          },
          onChange: val => {
            this.value = val;
            this.onSave();
          },
          onCancel: () => {
            this.onCancel();
          },
        },
      };
    },
    onScrollPlaceholder(event) {
      // stop event bubbling to allow scrolling inside editor
      event.stopPropagation();
    },
  },
};
</script>

<style module src="../styles/gridEditor.less" lang="less"></style>
